import React from 'react';
import {
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Button,
  Typography,
  Box,
  Slide,
  Zoom,
  Fade,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Close, Warning, Error } from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { styled, SxProps, Theme } from '@mui/material/styles';
const StyledDialog = styled(MuiDialog)<{
  variant?: 'default' | 'alert' | 'confirmation' | 'form';
  size?: 'small' | 'medium' | 'large' | 'fullscreen';
}>(({ theme, variant = 'default', size = 'medium' }) => ({
  '& .MuiDialog-paper': {
    borderRadius: `calc(${theme.shape.borderRadius}px * 2)`,
    ...(size === 'small' && {
      minWidth: 320,
      maxWidth: 400,
    }),
    ...(size === 'medium' && {
      minWidth: 400,
      maxWidth: 600,
    }),
    ...(size === 'large' && {
      minWidth: 600,
      maxWidth: 900,
    }),
    ...(size === 'fullscreen' && {
      margin: theme.spacing(2),
      maxHeight: `calc(100vh - ${theme.spacing(4)})`,
    }),
    ...(variant === 'alert' && {
      '& .MuiDialogTitle-root': {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
      },
    }),
    ...(variant === 'confirmation' && {
      '& .MuiDialogTitle-root': {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
      },
    }),
  },
}));
const DialogHeader = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  '& .MuiTypography-root': {
    fontWeight: 600,
  },
}));
// Transition components
const SlideTransition = React.forwardRef<
  unknown,
  TransitionProps & { children: React.ReactElement }
>((props, ref) => <Slide direction="up" ref={ref} {...props} />);
const ZoomTransition = React.forwardRef<
  unknown,
  TransitionProps & { children: React.ReactElement }
>((props, ref) => <Zoom ref={ref} {...props} />);
const FadeTransition = React.forwardRef<
  unknown,
  TransitionProps & { children: React.ReactElement }
>((props, ref) => <Fade ref={ref} {...props} />);
SlideTransition.displayName = 'SlideTransition';
ZoomTransition.displayName = 'ZoomTransition';
FadeTransition.displayName = 'FadeTransition';
export interface DialogAction {
  /**
   * Action label
   */
  label: string;
  /**
   * Click handler
   */
  onClick?: () => void;
  /**
   * Button variant
   */
  variant?: 'text' | 'outlined' | 'contained';
  /**
   * Button color
   */
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  /**
   * Whether action is disabled
   */
  disabled?: boolean;
  /**
   * Whether this is a loading action
   */
  loading?: boolean;
  /**
   * Whether to auto-focus this action
   */
  autoFocus?: boolean;
  /**
   * Icon for the action
   */
  icon?: React.ReactNode;
}
export interface DialogProps {
  /**
   * Whether dialog is open
   */
  open: boolean;
  /**
   * Callback fired when dialog should close
   */
  onClose?: () => void;
  /**
   * Dialog title
   */
  title?: React.ReactNode;
  /**
   * Dialog subtitle
   */
  subtitle?: string;
  /**
   * Dialog content
   */
  children?: React.ReactNode;
  /**
   * Content text (alternative to children)
   */
  content?: string;
  /**
   * Dialog variant
   */
  variant?: 'default' | 'alert' | 'confirmation' | 'form';
  /**
   * Dialog size
   */
  size?: 'small' | 'medium' | 'large' | 'fullscreen';
  /**
   * Dialog actions
   */
  actions?: DialogAction[];
  /**
   * Whether to show close button
   */
  showCloseButton?: boolean;
  /**
   * Close button label for accessibility
   */
  closeButtonLabel?: string;
  /**
   * Whether dialog can be closed by clicking backdrop
   */
  disableBackdropClick?: boolean;
  /**
   * Whether dialog can be closed by pressing escape
   */
  disableEscapeKeyDown?: boolean;
  /**
   * Transition animation
   */
  transition?: 'slide' | 'zoom' | 'fade' | 'none';
  /**
   * Maximum width
   */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  /**
   * Whether to take full width
   */
  fullWidth?: boolean;
  /**
   * Whether to take full screen on mobile
   */
  fullScreen?: boolean;
  /**
   * Icon to display in header
   */
  icon?: React.ReactNode;
  /**
   * Whether dialog is scrollable
   */
  scroll?: 'paper' | 'body';
  /**
   * Custom styling
   */
  sx?: SxProps<Theme>;
}
const getVariantIcon = (variant: DialogProps['variant']) => {
  switch (variant) {
    case 'alert':
      return <Error color="error" />;
    case 'confirmation':
      return <Warning color="warning" />;
    default:
      return null;
  }
};
const getTransitionComponent = (transition: DialogProps['transition']) => {
  switch (transition) {
    case 'slide':
      return SlideTransition;
    case 'zoom':
      return ZoomTransition;
    case 'fade':
      return FadeTransition;
    default:
      return undefined;
  }
};
export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  content,
  variant = 'default',
  size = 'medium',
  actions = [],
  showCloseButton = true,
  closeButtonLabel = 'Close',
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  transition = 'slide',
  maxWidth = 'sm',
  fullWidth = true,
  fullScreen: fullScreenProp = false,
  icon,
  scroll = 'paper',
  sx,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fullScreen = fullScreenProp || (isMobile && size !== 'small');
  const handleClose = () => {
    if (!disableBackdropClick) {
      onClose?.();
    }
  };
  const TransitionComponent = getTransitionComponent(transition);
  const variantIcon = icon || getVariantIcon(variant);
  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      variant={variant}
      size={size}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      scroll={scroll}
      TransitionComponent={TransitionComponent}
      disableEscapeKeyDown={disableEscapeKeyDown}
      sx={sx}
    >
      {(title || showCloseButton) && (
        <DialogHeader>
          <Box display="flex" alignItems="center" gap={1}>
            {variantIcon}
            <Box>
              {title && (
                <Typography variant="h4" component="h4">
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="body2" color="textSecondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          {showCloseButton && onClose && (
            <IconButton
              onClick={onClose}
              size="small"
              aria-label={closeButtonLabel}
              sx={{ ml: 1 }}
            >
              <Close />
            </IconButton>
          )}
        </DialogHeader>
      )}
      <DialogContent dividers={scroll === 'paper'}>
        {content && <DialogContentText>{content}</DialogContentText>}
        {children}
      </DialogContent>
      {actions.length > 0 && (
        <DialogActions sx={{ p: 2, gap: 1 }}>
          {actions.map((action, index) => (
            <Button
              key={index}
              variant={action.variant || 'text'}
              color={action.color || 'primary'}
              onClick={action.onClick}
              disabled={action.disabled || action.loading}
              autoFocus={action.autoFocus}
              startIcon={action.icon}
              sx={{ minWidth: 80 }}
            >
              {action.label}
            </Button>
          ))}
        </DialogActions>
      )}
    </StyledDialog>
  );
}; 